import React, { Component } from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "@chartLib/lib/GenericChartComponent";
import { functor } from "@chartLib/lib/utils";

// Static styles to prevent recreation on every render
const POINTER_EVENTS_ALL = { pointerEvents: "all" };
const TEXT_STYLE = { fontSize: 12, pointerEvents: "none" };
const BUTTON_STYLE_ENABLED = { cursor: "pointer", pointerEvents: "all" };
const BUTTON_STYLE_DISABLED = { cursor: "not-allowed", pointerEvents: "all" };

class ChartButtonsTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.handleUpClick = this.handleUpClick.bind(this);
    this.handleDownClick = this.handleDownClick.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
  }

  handleUpClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onUpClick, canMoveUp } = this.props;
    if (onUpClick && canMoveUp) {
      onUpClick();
    }
  }

  handleDownClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onDownClick, canMoveDown } = this.props;
    if (onDownClick && canMoveDown) {
      onDownClick();
    }
  }

  handleEditClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onEdit } = this.props;
    if (onEdit) onEdit(e);
  }

  handleDeleteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onDelete } = this.props;
    if (onDelete) onDelete(e);
  }

  renderSVG(moreProps) {
    const {
      className,
      buttonSize,
      arrowFill,
      showHorizontalLine,
      horizontalLineStrokeWidth,
      canMoveUp,
      canMoveDown,
      showEdit,
      showDelete,
    } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;
    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [, y] = origin(width, height);

    // Position buttons on the right side of the chart
    const buttons = [
      {
        key: "up",
        onClick: this.handleUpClick,
        enabled: canMoveUp,
        render: (fill) => (
          <polygon
            points={`${buttonSize / 2},${buttonSize / 4} ${(buttonSize * 3) / 4},${(buttonSize * 3) / 4} ${buttonSize / 4},${(buttonSize * 3) / 4}`}
            fill={fill}
            opacity={canMoveUp ? 1 : 0.5}
            style={POINTER_EVENTS_ALL}
          />
        ),
        title: "Mover gráfico para cima",
      },
      {
        key: "down",
        onClick: this.handleDownClick,
        enabled: canMoveDown,
        render: (fill) => (
          <polygon
            points={`${buttonSize / 4},${buttonSize / 4} ${(buttonSize * 3) / 4},${buttonSize / 4} ${buttonSize / 2},${(buttonSize * 3) / 4}`}
            fill={fill}
            opacity={canMoveDown ? 1 : 0.5}
            style={POINTER_EVENTS_ALL}
          />
        ),
        title: "Mover gráfico para baixo",
      },
    ];

    if (showEdit) {
      buttons.push({
        key: "edit",
        onClick: this.handleEditClick,
        enabled: true,
        render: (fill) => (
          <text
            x={buttonSize / 2}
            y={buttonSize / 2 + 5}
            textAnchor="middle"
            fill={fill}
            style={TEXT_STYLE}
          >
            ✎
          </text>
        ),
        title: "Editar indicador",
      });
    }

    if (showDelete) {
      buttons.push({
        key: "delete",
        onClick: this.handleDeleteClick,
        enabled: true,
        render: (fill) => (
          <text
            x={buttonSize / 2}
            y={buttonSize / 2 + 5}
            textAnchor="middle"
            fill={fill}
            style={TEXT_STYLE}
          >
            🗑
          </text>
        ),
        title: "Remover indicador",
      });
    }

    const buttonSpacing = buttonSize + 4;
    const totalWidth = buttons.length * buttonSize + (buttons.length - 1) * 4;
    const rightAlignedX = width - totalWidth - 12;

    return (
      <g
        className={className}
        transform={`translate(${rightAlignedX}, ${y})`}
        style={POINTER_EVENTS_ALL}
      >
        {/* Horizontal line at Y=2 for indicator charts */}
        {showHorizontalLine && (
          <line
            x1={-rightAlignedX} // Start from left edge (0)
            y1={2} // Fixed Y position
            x2={width - rightAlignedX} // End at right edge (full width)
            y2={2} // Fixed Y position
            stroke="pink"
            strokeWidth={horizontalLineStrokeWidth}
            opacity={1}
          />
        )}
        {buttons.map((button, idx) => {
          const xTranslate = idx * buttonSpacing;
          const enabled = button.enabled !== false;
          const fill = enabled ? arrowFill : "#888888";
          const onClick = enabled ? button.onClick : undefined;

          return (
            <g
              key={button.key}
              onClick={onClick}
              style={enabled ? BUTTON_STYLE_ENABLED : BUTTON_STYLE_DISABLED}
              transform={`translate(${xTranslate}, 0)`}
              className={`chart-button chart-button-${button.key}`}
              data-evo-id={`chartbuttonstooltip-btn-${button.key}`}
            >
              <rect
                x={0}
                y={0}
                width={buttonSize}
                height={buttonSize}
                fill="transparent"
                style={POINTER_EVENTS_ALL}
              />
              {button.render(fill)}
              <rect
                x={0}
                y={0}
                width={buttonSize}
                height={buttonSize}
                fill="transparent"
                style={{ pointerEvents: "all" }}
              >
                <title>{button.title}</title>
              </rect>
            </g>
          );
        })}
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        canvasToDraw={this.getCanvasToDraw}
        drawOn={[]}
      />
    );
  }

  getCanvasToDraw() {
    return [];
  }
}

ChartButtonsTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  buttonFill: PropTypes.string,
  buttonStroke: PropTypes.string,
  buttonStrokeWidth: PropTypes.number,
  buttonSize: PropTypes.number,
  arrowFill: PropTypes.string,
  fontSize: PropTypes.number,
  onUpClick: PropTypes.func,
  onDownClick: PropTypes.func,
  showHorizontalLine: PropTypes.bool,
  horizontalLineStrokeWidth: PropTypes.number,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
  showEdit: PropTypes.bool,
  showDelete: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

ChartButtonsTooltip.defaultProps = {
  origin: [0, 0],
  className: "chart-buttons-tooltip",
  buttonSize: 16,
  arrowFill: "#495057",
  fontSize: 12,
  onUpClick: null,
  onDownClick: null,
  showHorizontalLine: false,
  horizontalLineStrokeWidth: 1,
  canMoveUp: true,
  canMoveDown: true,
  showEdit: false,
  showDelete: false,
  onEdit: null,
  onDelete: null,
};

export default ChartButtonsTooltip;
